import React, { useState, useEffect, createContext, useContext, ReactNode, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import SessionTokenManager from '@/services/sessionTokenManager';
import { SessionManager } from '@/services/sessionManager';
import { debugSession, debugError } from '@/lib/debug';

export interface PlayerSession {
  playerId: string;
  playerName: string;
  groupId: string;
  groupName: string;
  sessionToken: string;
  expiresAt: string;
}

interface PlayerAuthContextType {
  playerSession: PlayerSession | null;
  loading: boolean;
  sessionReady: boolean;
  authenticatePlayer: (playerName: string, passcode: string) => Promise<{ success: boolean; error?: string }>;
  authenticateAsDefaultPlayer: () => Promise<{ success: boolean; error?: string }>;
  signOutPlayer: () => Promise<void>;
  // New: manually trigger session bootstrap (for authenticated routes)
  triggerSessionBootstrap: () => Promise<void>;
}

const PlayerAuthContext = createContext<PlayerAuthContextType | undefined>(undefined);

// Routes that are considered "public" - no session bootstrap needed
const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/contact',
  '/partners',
  '/tiimista',
  '/maintenance',
  '/auth',
  '/auth/callback',
  '/accept-invite',
  '/pelaa',
  '/login',
  '/join-school',
  '/activate-license',
  '/demo',
  '/demo/choice',
  '/demo/teacher',
];

function isPublicRoute(pathname: string): boolean {
  // Exact matches
  if (PUBLIC_ROUTES.includes(pathname)) return true;
  // Dev routes
  if (pathname.startsWith('/dev/')) return true;
  return false;
}

export const PlayerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [playerSession, setPlayerSession] = useState<PlayerSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const queryClient = useQueryClient();
  
  // Track session bootstrap state
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);
  const bootstrapTriggeredRef = useRef(false);
  const isBootstrappingRef = useRef(false);

  // Core session check logic - extracted for reuse
  const checkStoredSession = async (): Promise<boolean> => {
    if (isBootstrappingRef.current) {
      debugSession('Bootstrap already in progress, skipping');
      return false;
    }
    
    isBootstrappingRef.current = true;
    
    try {
      setLoading(true);
      setSessionReady(false);
      
      // Check if we have a persisted token
      const persistedToken = SessionTokenManager.getPersistedToken();
      
      if (persistedToken) {
        debugSession('Found persisted token, bootstrapping');
        
        // Use the new bootstrap method - player_id will be fetched from server
        const bootstrapResult = await SessionTokenManager.bootstrap();
        
        if (bootstrapResult) {
          // Token is valid and set in DB - now fetch player info
          const { data, error } = await supabase.rpc('validate_player_session', {
            p_session_token: bootstrapResult.token
          });

          if (data && data.length > 0 && !error) {
            const sessionData = data[0];
            
            const restoredSession = {
              playerId: sessionData.player_id,
              playerName: sessionData.display_name,
              groupId: sessionData.group_id,
              groupName: sessionData.group_name,
              sessionToken: bootstrapResult.token,
              expiresAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // 4 hours
            };
            setPlayerSession(restoredSession);
            
            // Sync with SessionManager
            SessionManager.setSession({
              sessionToken: bootstrapResult.token,
              playerId: sessionData.player_id,
              playerName: sessionData.display_name,
              groupId: sessionData.group_id,
              expiresAt: restoredSession.expiresAt
            });
            
            setSessionReady(true);
            
            debugSession('Session restored', { source: bootstrapResult.source, playerId: sessionData.player_id.slice(-6) });
            
            setSessionCheckComplete(true);
            setLoading(false);
            return true;
          } else {
            // Session invalid - clear and require re-auth
            console.log('[PlayerAuth] Bootstrap succeeded but validate failed, requiring re-auth');
            
            // Use softReset to clear token
            SessionTokenManager.softReset();
            
            // Session expired, user needs to re-authenticate via login
            debugSession('Session invalid, not falling back to Default Player');
            setSessionCheckComplete(true);
            setLoading(false);
            return false;
          }
        } else {
          debugSession('Bootstrap failed, will try auto-auth');
        }
      } else {
        // Also check old localStorage keys for migration
        const oldToken = localStorage.getItem('player_session_token');
        if (oldToken) {
          debugSession('Found old token, attempting migration');
          const { data, error } = await supabase.rpc('validate_player_session', {
            p_session_token: oldToken
          });

          if (data && data.length > 0 && !error) {
            const sessionData = data[0];
            // Migrate to new keys
            SessionTokenManager.persistToken(oldToken, sessionData.player_id, 
              localStorage.getItem('player_session_expires') || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
            );
            // Bootstrap with migrated token
            await SessionTokenManager.bootstrap(sessionData.player_id);
            
            const restoredSession = {
              playerId: sessionData.player_id,
              playerName: sessionData.display_name,
              groupId: sessionData.group_id,
              groupName: sessionData.group_name,
              sessionToken: oldToken,
              expiresAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // 4 hours
            };
            setPlayerSession(restoredSession);
            
            SessionManager.setSession({
              sessionToken: oldToken,
              playerId: sessionData.player_id,
              playerName: sessionData.display_name,
              groupId: sessionData.group_id,
              expiresAt: restoredSession.expiresAt
            });
            
            setSessionReady(true);
            
            // Clean up old keys
            localStorage.removeItem('player_session_token');
            localStorage.removeItem('player_session_expires');
            
            debugSession('Migrated old session successfully');
            
            setSessionCheckComplete(true);
            setLoading(false);
            return true;
          } else {
            // Old token invalid, clean up
            localStorage.removeItem('player_session_token');
            localStorage.removeItem('player_session_expires');
          }
        }
      }
    } catch (error) {
      debugError('Error in session check', error);
      SessionTokenManager.reset();
    } finally {
      isBootstrappingRef.current = false;
    }
    
    // Mark check as complete
    setSessionCheckComplete(true);
    setLoading(false);
    return false;
  };

  // Manual trigger for authenticated routes
  const triggerSessionBootstrap = useCallback(async () => {
    const hasPersistedToken = !!SessionTokenManager.getPersistedToken();
    const shouldRetryAfterLogin = hasPersistedToken && !playerSession?.sessionToken;

    if (bootstrapTriggeredRef.current && !shouldRetryAfterLogin) {
      debugSession('Bootstrap already triggered, skipping');
      return;
    }

    bootstrapTriggeredRef.current = true;
    
    if (import.meta.env.DEV) {
      console.log('[PlayerAuth] triggerSessionBootstrap called');
    }
    
    await checkStoredSession();
  }, [playerSession?.sessionToken]);

  // Auto-authenticate as default player for logged-in Supabase users
  // ONLY runs AFTER session check is complete AND we're on an authenticated route
  useEffect(() => {
    const autoAuthenticateDefaultPlayer = async () => {
      // Wait for session check to complete first
      if (!sessionCheckComplete) {
        return;
      }
      
      // Only proceed if no player session exists and not currently loading
      if (playerSession?.sessionToken || loading) {
        return;
      }

      // Check if we had a previous player - don't auto-switch to Default Player
      const cachedPlayerId = SessionTokenManager.getCachedPlayerId();
      if (cachedPlayerId) {
        debugSession('Had previous player, NOT auto-switching', { playerId: cachedPlayerId.slice(-6) });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        debugSession('Auto-authenticating as default player');
        await authenticateAsDefaultPlayer();
      }
    };

    autoAuthenticateDefaultPlayer();
  }, [sessionCheckComplete, playerSession?.sessionToken, loading]);

  const authenticatePlayer = async (playerName: string, passcode: string) => {
    try {
      setLoading(true);
      setSessionReady(false);

      // First authenticate the player
      const { data: authData, error: authError } = await supabase.rpc('authenticate_player', {
        p_display_name: playerName,
        p_passcode: passcode
      });

      if (authError || !authData || authData.length === 0) {
        return { 
          success: false, 
          error: 'Invalid player name or passcode' 
        };
      }

      // Check for specific error reasons from the RPC
      const authResult = authData[0] as { 
        player_id: string | null; 
        group_id: string | null; 
        group_name: string | null; 
        success: boolean; 
        error_reason: string | null;
      };
      
      if (authResult.success === false) {
        const reason = authResult.error_reason;
        if (reason === 'too_many_attempts') {
          return {
            success: false,
            error: 'Liikaa yrityksiä. Odota 10 minuuttia ja yritä uudelleen.'
          };
        }
        if (reason === 'invalid_credentials') {
          return {
            success: false,
            error: 'Virheellinen nimi tai koodi.'
          };
        }
        return {
          success: false,
          error: 'Kirjautuminen epäonnistui. Tarkista tiedot.'
        };
      }

      const playerData = authResult;

      // Use createSession to create a new session (after passcode auth)
      const sessionResult = await SessionTokenManager.createSession(playerData.player_id);
      
      if (!sessionResult) {
        return { 
          success: false, 
          error: 'Failed to create session' 
        };
      }
      
      // Store session data - TTL is now 4 hours
      const session: PlayerSession = {
        playerId: playerData.player_id,
        playerName: playerName,
        groupId: playerData.group_id,
        groupName: playerData.group_name,
        sessionToken: sessionResult.token,
        expiresAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // 4 hours
      };

      setPlayerSession(session);
      
      // Sync with SessionManager
      SessionManager.setSession({
        sessionToken: sessionResult.token,
        playerId: playerData.player_id,
        playerName: playerName,
        groupId: playerData.group_id,
        expiresAt: session.expiresAt
      });
      
      setSessionReady(true);

      return { success: true };
    } catch (error) {
      debugError('Player authentication error', error);
      return { 
        success: false, 
        error: 'Authentication failed. Please try again.' 
      };
    } finally {
      setLoading(false);
    }
  };

  const authenticateAsDefaultPlayer = async (): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true);
      setSessionReady(false);

      // Get the current authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Find the default player for this user
      const { data: players, error: playerError } = await supabase
        .from('players')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .eq('is_active', true);

      if (playerError || !players || players.length === 0) {
        return { success: false, error: 'No default player found' };
      }

      const player = players[0];

      // Use createSession to create a new session
      const sessionResult = await SessionTokenManager.createSession(player.id);
      
      if (!sessionResult) {
        return { success: false, error: 'Failed to create session' };
      }
      
      // Store session data - TTL is now 4 hours
      const playerSessionData: PlayerSession = {
        playerId: player.id,
        playerName: player.display_name,
        groupId: player.player_group_id || '',
        groupName: '',
        sessionToken: sessionResult.token,
        expiresAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // 4 hours
      };

      setPlayerSession(playerSessionData);
      
      // Sync with SessionManager
      SessionManager.setSession({
        sessionToken: sessionResult.token,
        playerId: player.id,
        playerName: player.display_name,
        groupId: player.player_group_id,
        expiresAt: playerSessionData.expiresAt
      });
      
      setSessionReady(true);

      debugSession('Default player authenticated', { playerId: player.id.slice(-6) });

      return { success: true };
    } catch (error) {
      debugError('Error authenticating as default player', error);
      return { success: false, error: 'Authentication failed' };
    } finally {
      setLoading(false);
    }
  };

  const signOutPlayer = async () => {
    try {
      // Reset session token manager (also clears localStorage)
      SessionTokenManager.reset();
      
      // Clear React Query cache to prevent stale data on re-login
      queryClient.clear();
      
      // Clear SessionManager
      SessionManager.clearSession();
      
      // Reset refs
      bootstrapTriggeredRef.current = false;
      
      // Clear state
      setPlayerSession(null);
      setSessionReady(false);
      setSessionCheckComplete(false);
    } catch (error) {
      debugError('Player sign out error', error);
    }
  };

  // For public routes, set loading=false immediately without bootstrap
  useEffect(() => {
    // Only run this initialization once
    if (bootstrapTriggeredRef.current) return;
    
    // Check if we should skip bootstrap on this route
    // This is a fallback for when provider mounts before router
    const pathname = window.location.pathname;
    
    if (isPublicRoute(pathname)) {
      if (import.meta.env.DEV) {
        console.log('[PlayerAuth] Public route detected, skipping bootstrap:', pathname);
      }
      // Don't bootstrap, just mark as ready
      setLoading(false);
      setSessionCheckComplete(true);
    }
    // For non-public routes, bootstrap will be triggered by AuthenticatedApp
  }, []);

  // Demo mode: the demo player_session token is persisted via SessionTokenManager
  // by useDemoSession.validate(), so the regular bootstrap flow above picks it up
  // automatically — no special handling needed here.

  const contextValue = useMemo(() => ({
    playerSession,
    loading,
    sessionReady,
    authenticatePlayer,
    authenticateAsDefaultPlayer,
    signOutPlayer,
    triggerSessionBootstrap,
  }), [playerSession, loading, sessionReady]);

  return (
    <PlayerAuthContext.Provider value={contextValue}>
      {children}
    </PlayerAuthContext.Provider>
  );
};

export const usePlayerAuth = () => {
  const context = useContext(PlayerAuthContext);
  if (context === undefined) {
    throw new Error('usePlayerAuth must be used within a PlayerAuthProvider');
  }
  return context;
};
